import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const inputCls =
    "w-full border border-white/15 bg-navy-deep px-4 py-3 text-sm text-ivory placeholder:text-slate-500 focus:border-gold focus:outline-none transition-colors";

export function Field({ label, htmlFor, required, children, hint }) {
    return (
        <div>
            <label htmlFor={htmlFor} className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                {label} {required && <span className="text-gold">*</span>}
            </label>
            {children}
            {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
        </div>
    );
}

export function FileField({ id, file, onChange }) {
    const handle = (e) => {
        const f = e.target.files?.[0];
        if (!f) return onChange(null);
        if (f.size > 3 * 1024 * 1024) {
            toast.error("File is too large — maximum 3 MB (PDF, PNG or JPG).");
            e.target.value = "";
            return;
        }
        if (!["application/pdf", "image/png", "image/jpeg"].includes(f.type)) {
            toast.error("Only PDF, PNG or JPG files are accepted.");
            e.target.value = "";
            return;
        }
        const reader = new FileReader();
        reader.onload = () => onChange({ name: f.name, type: f.type, data: String(reader.result).split(",")[1] });
        reader.readAsDataURL(f);
    };
    return (
        <div>
            <input
                id={id}
                data-testid={id}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handle}
                className="w-full border border-dashed border-white/20 bg-navy-deep px-4 py-3 text-sm text-slate-400 file:mr-4 file:border-0 file:bg-gold file:px-4 file:py-2 file:text-xs file:font-bold file:uppercase file:tracking-widest file:text-navy-deep"
            />
            {file && <p className="mt-2 text-xs text-gold">Attached: {file.name}</p>}
        </div>
    );
}

export async function submitEnquiry(category, data, file, consent) {
    const r = await axios.post(`${API}/enquiries`, { category, data, file, consent });
    return r.data;
}

export function SuccessPanel({ reference, categoryLabel }) {
    return (
        <div className="border border-gold/40 bg-gold/5 p-10 text-center lg:p-14" data-testid="enquiry-success">
            <CheckCircle2 size={36} className="mx-auto text-gold" aria-hidden="true" />
            <h2 className="mt-6 font-serif text-3xl font-light text-ivory">Enquiry Received</h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-300/85">
                Thank you. Your {categoryLabel} enquiry has been received and will be directed to the
                appropriate specialist team. Your reference is <span className="font-semibold text-gold">{reference}</span>.
                A confirmation email is on its way to the address you provided.
            </p>
            <p className="mx-auto mt-4 max-w-xl text-xs leading-relaxed text-slate-500">
                Our team reviews enquiries during normal working hours. Submitting an enquiry does not guarantee
                acceptance, treatment, supply or a specific response time.
            </p>
        </div>
    );
}

export function useEnquiryForm(category, categoryLabel) {
    const [submitting, setSubmitting] = useState(false);
    const [reference, setReference] = useState(null);

    const submit = async (data, file, consent) => {
        setSubmitting(true);
        try {
            const r = await submitEnquiry(category, data, file, consent);
            setReference(r.reference);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Submission failed — please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return { submitting, reference, submit, categoryLabel };
}
